import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeckMetrics from './deck_metrics';
import { addColor, prepareMetrics, removeColor } from './services';
import {
  getCurrentProvider,
  providers
} from '../../../services/provider/getCurrentProvider';
import useSettings from '../../../libs/settings/useSettings';
import {
  globalMetricsAll,
  fetchMetrics
} from '../../../redux/overview/overviewActions';
import { getCurrentLabel } from '../../../redux/app/appActions';
import {
  getOverviewGMPreferences,
  getVrrPreferences
} from '../../../redux/settings/settingAction';
import { TSelectionMetric } from './deck_metrics/card_metric/types';
import { TMetric } from '../../../redux/settings/types';

const GlobalMetrics: React.FC = () => {
  const dispatch: any = useDispatch();
  const allMetrics = useSelector(globalMetricsAll);
  const selectedMetrics = useSelector(getOverviewGMPreferences);
  const preference = useSelector(getVrrPreferences);
  const datePickerPeriod = useSelector(getVrrPreferences)?.period;
  const funnel = useSelector(getCurrentLabel);
  const [brand, setBrand] = useState<string | undefined>(undefined);
  const [period, setPeriod] = useState(datePickerPeriod);
  const [currentFunnel, setCurrentFunnel] = useState(funnel);
  const [metrics, setMetrics] = useState<TMetric[] | undefined>(undefined);
  const [metricColors, setMetricColors] = useState([
    'turquoise',
    'mauve',
    'yellow'
  ]);
  const [provider, setProvider] = useState<string>('all');
  const { overview } = useSettings();

  useEffect(() => {
    const currentProvider = getCurrentProvider();
    if (providers.includes(currentProvider)) {
      setProvider(currentProvider);
    }
  });

  useEffect(() => {
    if (
      preference?.current_brand_id !== brand ||
      period?.from !== datePickerPeriod?.from ||
      period?.to !== datePickerPeriod?.to ||
      funnel !== currentFunnel
    ) {
      setBrand(preference?.current_brand_id);
      setPeriod(datePickerPeriod);
      setCurrentFunnel(funnel);
    }
  }, [datePickerPeriod, preference, funnel]);

  useEffect(() => {
    if (
      brand &&
      period?.from &&
      period?.to &&
      currentFunnel &&
      provider !== ''
    ) {
      dispatch(fetchMetrics({ brandId: brand, period, p: provider }));
    }
  }, [period, brand, currentFunnel, provider]);

  useEffect(() => {
    if (!!selectedMetrics?.metrics?.length && !metrics?.length) {
      setMetrics(selectedMetrics?.metrics);
    }
  }, [selectedMetrics]);

  useEffect(() => {
    if (!!metrics?.length) {
      const newMetricColors = [...metricColors];
      metrics.forEach((metric) => {
        if (metric.color) {
          const index = newMetricColors.indexOf(metric.color);
          if (index > -1 && newMetricColors.length > 0) {
            newMetricColors.splice(index, 1);
          }
        }
      });
      setMetricColors(newMetricColors);
    }
  }, [metrics]);

  const updatePreferences = (
    newSetting: {
      name: string | number;
      color?: string;
    }[]
  ): void => {
    if (newSetting) {
      overview('global_metrics', { metrics: newSetting });
    }
  };

  const onUpdate = (dataStore: TSelectionMetric[]): void => {
    const newSetting = dataStore
      .filter((metric: TSelectionMetric) => !!metric.selected)
      .map((metric: TSelectionMetric) => {
        return { name: metric.name.value, color: metric?.color };
      });

    setMetricColors(['turquoise', 'mauve', 'yellow']);
    setMetrics(newSetting);
    updatePreferences(newSetting);
  };

  const changeColor = (name: string, color: string) => {
    const all = prepareMetrics(allMetrics, selectedMetrics);
    const newDataStore: TSelectionMetric[] | undefined = all?.map((metric) => {
      const newMetric = { ...metric };
      if (newMetric.name.value === name) {
        if (color === 'grey' && metricColors.length > 0) {
          const newColor = metricColors[0];
          removeColor(newColor, metricColors, setMetricColors);
          return {
            ...newMetric,
            color: newColor
          };
        }
        if (newMetric?.color) {
          delete newMetric?.color;
          addColor(color, metricColors, setMetricColors);
        }
      }
      return newMetric;
    });
    const newSetting = newDataStore
      ?.filter((metric: TSelectionMetric) => !!metric.selected)
      .map((metric: TSelectionMetric) => {
        return { name: metric.name.value, color: metric?.color };
      });
    if (newSetting) updatePreferences(newSetting);
  };

  return (
    <DeckMetrics
      metrics={prepareMetrics(allMetrics, selectedMetrics)}
      onSubmitAction={onUpdate}
      onClickAction={changeColor}
    />
  );
};

export default GlobalMetrics;
